import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { EyeIcon, PackagePlusIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { isExpired, isExpiringWithin, stockStatus } from './pharmacyUtils';
type MedicineMasterForm = {
  genericName: string;
  brandName: string;
  strength: string;
  dosageForm: string;
  unit: string;
  packSize: string;
  category: string;
  manufacturer: string;
  buyingPrice: string;
  sellingPrice: string;
  reorderLevel: string;
  maximumStock: string;
  status: 'Active' | 'Inactive';
};
type InventoryRow = {
  id: string;
  medicineId: string;
  drugCode: string;
  medicineName: string;
  category: string;
  supplier: string;
  batchNumber: string;
  quantity: number;
  unit: string;
  reorderLevel: number;
  expiryDate: string;
  status: string;
  isBatch: boolean;
};
const initialForm: MedicineMasterForm = {
  genericName: '',
  brandName: '',
  strength: '',
  dosageForm: 'Tablet',
  unit: 'Tablet',
  packSize: '1',
  category: 'General',
  manufacturer: '',
  buyingPrice: '',
  sellingPrice: '',
  reorderLevel: '20',
  maximumStock: '100',
  status: 'Active'
};
type InventoryListProps = {
  readOnly?: boolean;
};
export function InventoryList({
  readOnly = false
}: InventoryListProps) {
  const {
    medicines,
    medicineBatches,
    suppliers,
    addMedicine
  } = useData();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [supplier, setSupplier] = useState('');
  const [expiry, setExpiry] = useState('all');
  const [stock, setStock] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<MedicineMasterForm>(initialForm);
  const [selectedRow, setSelectedRow] = useState<InventoryRow | null>(null);
  const rows = useMemo(() => {
    const allRows: InventoryRow[] = medicines.flatMap(medicine => {
      const batches = medicineBatches.filter(batch => batch.medicineId === medicine.id);
      if (!batches.length) return [{
        id: `master-${medicine.id}`,
        medicineId: medicine.id,
        drugCode: medicine.drugCode,
        medicineName: medicine.name,
        category: medicine.category,
        supplier: '',
        batchNumber: 'No batch recorded',
        quantity: medicine.currentQuantity,
        unit: medicine.unit,
        reorderLevel: medicine.reorderLevel,
        expiryDate: '',
        status: stockStatus(medicine),
        isBatch: false
      }];
      return batches.map(batch => {
        const state = isExpired(batch) ? 'Expired' : isExpiringWithin(batch, 90) ? 'Near expiry' : batch.quantityRemaining <= 0 ? 'Out of stock' : medicine.currentQuantity <= medicine.reorderLevel ? 'Low stock' : 'In stock';
        return {
          id: batch.id,
          medicineId: medicine.id,
          drugCode: medicine.drugCode,
          medicineName: medicine.name,
          category: medicine.category,
          supplier: batch.supplierId || '',
          batchNumber: batch.batchNumber,
          quantity: batch.quantityRemaining,
          unit: medicine.unit,
          reorderLevel: medicine.reorderLevel,
          expiryDate: batch.expiryDate,
          status: state,
          isBatch: true
        };
      });
    });
    const normalizedQuery = query.trim().toLowerCase();
    return allRows.filter(row => {
      const matchesQuery = !normalizedQuery || `${row.drugCode} ${row.medicineName} ${row.batchNumber} ${row.category}`.toLowerCase().includes(normalizedQuery);
      const matchesCategory = !category || row.category === category;
      const matchesSupplier = !supplier || row.supplier === supplier;
      const matchesExpiry = expiry === 'all' || expiry === 'near' && row.isBatch && row.status === 'Near expiry' || expiry === 'expired' && row.isBatch && row.status === 'Expired';
      const matchesStock = !stock || row.status === stock;
      return matchesQuery && matchesCategory && matchesSupplier && matchesExpiry && matchesStock;
    }).sort((a, b) => a.medicineName.localeCompare(b.medicineName) || a.expiryDate.localeCompare(b.expiryDate));
  }, [category, expiry, medicineBatches, medicines, query, stock, supplier]);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const buyingPrice = Number(form.buyingPrice);
    const sellingPrice = Number(form.sellingPrice);
    const reorderLevel = Number(form.reorderLevel);
    const maximumStock = Number(form.maximumStock);
    if (!form.genericName.trim() || !form.brandName.trim() || !form.manufacturer.trim() || !Number.isFinite(buyingPrice) || buyingPrice < 0 || !Number.isFinite(sellingPrice) || sellingPrice < 0) return setError('Generic name, brand name, manufacturer, and valid buying and selling prices are required.');
    if (!Number.isFinite(reorderLevel) || reorderLevel < 0 || !Number.isFinite(maximumStock) || maximumStock < reorderLevel) return setError('Enter a maximum stock at or above the reorder level.');
    addMedicine({
      genericName: form.genericName.trim(),
      brandName: form.brandName.trim(),
      strength: form.strength.trim() || 'Not specified',
      dosageForm: form.dosageForm,
      unit: form.unit.trim() || form.dosageForm,
      packSize: Math.max(1, Number(form.packSize) || 1),
      category: form.category.trim() || 'General',
      manufacturer: form.manufacturer.trim(),
      buyingPrice,
      sellingPrice,
      currentQuantity: 0,
      reorderLevel,
      maximumStock,
      status: form.status
    });
    setForm(initialForm);
    setShowForm(false);
    setError('');
  };
  const categories = [...new Set(medicines.map(medicine => medicine.category))].sort();
  return <div className="space-y-5" data-id="element-4888" data-mp="1z2jlgk0uuz9a">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-4889" data-mp="1z3mbyqu9mzoo">
        <div data-id="element-4890" data-mp="2wogy40sk0adb">
          <p className="text-sm font-semibold text-pink-600" data-id="element-4891" data-mp="2wrczjz3m1h6i">
            Medicine master & batches
          </p>
          <h2 className="mt-1 text-xl font-bold text-slate-950" data-id="element-4892" data-mp="2mzgwe9mo30vf">
            Pharmacy Inventory
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-4893" data-mp="2i0ngkajwqxs3">
            Live batch balances are controlled by the inventory ledger. Drug
            Code and Medicine ID are generated centrally.
          </p>
        </div>
        {readOnly ? <span className="inline-flex w-fit items-center rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800" data-id="element-4894" data-mp="3ao9n9rofmij6">
            View-only access
          </span> : <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-4895" data-mp="3mzj2ftzow4cq">
            <PlusIcon className="mr-2 h-4 w-4" data-id="element-4896" data-mp="s2p9e7i7icd" /> Add medicine
          </Button>}
      </div>
      {!readOnly && showForm && <form onSubmit={submit} className="grid grid-cols-1 gap-4 rounded-xl border border-teal-200 bg-teal-50/50 p-5 md:grid-cols-2 xl:grid-cols-3" data-id="element-4897" data-mp="4smkgc8iobvv">
          <div className="flex items-center justify-between md:col-span-2 xl:col-span-3" data-id="element-4898" data-mp="eixorzw44n3a">
            <div data-id="element-4899" data-mp="jiakla4yp4i0">
              <h3 className="font-bold text-slate-900" data-id="element-4900" data-mp="jituc3kgkmky">
                New medicine master record
              </h3>
              <p className="mt-1 text-xs text-slate-500" data-id="element-4901" data-mp="oiya1rdjlda4">
                System-generated: Drug Code and Medicine ID.
              </p>
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-800" data-id="element-4902" data-mp="1du72rbb19e9z">
              Cancel
            </button>
          </div>
          {error && <p role="alert" className="text-sm text-red-600 md:col-span-2 xl:col-span-3" data-id="element-4903" data-mp="1nqaaqxskg74u">
              {error}
            </p>}
          <Input label="Generic name" required value={form.genericName} onChange={event => setForm({
        ...form,
        genericName: event.target.value
      })} data-id="element-4904" data-mp="v48qej07cz0j" />
          <Input label="Brand name" required value={form.brandName} onChange={event => setForm({
        ...form,
        brandName: event.target.value
      })} data-id="element-4905" data-mp="101r1og1ozqbp" />
          <Input label="Strength" placeholder="e.g. 500 mg" value={form.strength} onChange={event => setForm({
        ...form,
        strength: event.target.value
      })} data-id="element-4906" data-mp="2j0yfu0ycfow6" />
          <Select label="Dosage form" value={form.dosageForm} onChange={event => setForm({
        ...form,
        dosageForm: event.target.value,
        unit: form.unit === form.dosageForm ? event.target.value : form.unit
      })} options={['Tablet', 'Capsule', 'Syrup', 'Suspension', 'Injection', 'Cream', 'Ointment', 'Sachet'].map(value => ({
        value,
        label: value
      }))} data-id="element-4907" data-mp="2e23xt187a4d4" />
          <Input label="Unit" value={form.unit} onChange={event => setForm({
        ...form,
        unit: event.target.value
      })} data-id="element-4908" data-mp="22xai2mzh0t0p" />
          <Input label="Pack size" required type="number" min="1" value={form.packSize} onChange={event => setForm({
        ...form,
        packSize: event.target.value
      })} data-id="element-4909" data-mp="1t4bth53z52h3" />
          <Input label="Category" value={form.category} onChange={event => setForm({
        ...form,
        category: event.target.value
      })} data-id="element-4910" data-mp="1a7k1m7ad9f1f" />
          <Input label="Manufacturer" required value={form.manufacturer} onChange={event => setForm({
        ...form,
        manufacturer: event.target.value
      })} data-id="element-4911" data-mp="34mwf5n0wgu7e" />
          <Select label="Record status" value={form.status} onChange={event => setForm({
        ...form,
        status: event.target.value as MedicineMasterForm['status']
      })} options={[{
        value: 'Active',
        label: 'Active'
      }, {
        value: 'Inactive',
        label: 'Inactive'
      }]} data-id="element-4912" data-mp="3eqb89eo040dn" />
          <Input label="Buying price (ZMW)" required type="number" min="0" step="0.01" value={form.buyingPrice} onChange={event => setForm({
        ...form,
        buyingPrice: event.target.value
      })} data-id="element-4913" data-mp="f9v17shsndhb" />
          <Input label="Selling price (ZMW)" required type="number" min="0" step="0.01" value={form.sellingPrice} onChange={event => setForm({
        ...form,
        sellingPrice: event.target.value
      })} data-id="element-4914" data-mp="g01u2cjkuxpr" />
          <Input label="Reorder level" required type="number" min="0" value={form.reorderLevel} onChange={event => setForm({
        ...form,
        reorderLevel: event.target.value
      })} data-id="element-4915" data-mp="uupxll5f3m2q" />
          <Input label="Maximum stock" required type="number" min="0" value={form.maximumStock} onChange={event => setForm({
        ...form,
        maximumStock: event.target.value
      })} data-id="element-4916" data-mp="1dn0umyde4psx" />
          <div className="flex items-end justify-end md:col-span-2 xl:col-span-3" data-id="element-4917" data-mp="3772knz55wsq2">
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-id="element-4918" data-mp="33464sg70bapt">
              <PackagePlusIcon className="mr-2 h-4 w-4" data-id="element-4919" data-mp="2y52zcun472hb" /> Create medicine
            </Button>
          </div>
        </form>}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4920" data-mp="2t79tk2pq4ahk">
        <div className="grid grid-cols-1 gap-3 border-b border-slate-100 p-4 lg:grid-cols-5" data-id="element-4921" data-mp="3qsyv9x4v0qd1">
          <div className="relative lg:col-span-2" data-id="element-4922" data-mp="3lvyn9gog9dja">
            <SearchIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-4923" data-mp="3lx9r1ghwalae" />
            <input aria-label="Search pharmacy inventory" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search drug code, medicine or batch" className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600" data-id="element-4924" data-mp="3h1uin42nphld" />
          </div>
          <select aria-label="Filter by category" value={category} onChange={event => setCategory(event.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-id="element-4925" data-mp="3tdv6hfvzia3q">
            <option value="" data-id="element-4926" data-mp="3kerm88l2zonn">All categories</option>
            {categories.map(value => <option key={value} value={value} data-id="element-4927" data-mp="bxnc2mr0qlt7">
                {value}
              </option>)}
          </select>
          <select aria-label="Filter by supplier" value={supplier} onChange={event => setSupplier(event.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-id="element-4928" data-mp="2725ac16yaz7">
            <option value="" data-id="element-4929" data-mp="uu5311y1mhoe">All suppliers</option>
            {suppliers.map(value => <option key={value.id} value={value.id} data-id="element-4930" data-mp="zn7xrdek3x66">
                {value.name}
              </option>)}
          </select>
          <div className="flex gap-3" data-id="element-4931" data-mp="2kaihl8y2b4v0">
            <select aria-label="Filter by expiry" value={expiry} onChange={event => setExpiry(event.target.value)} className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-id="element-4932" data-mp="2fejxhxjro90e">
              <option value="all" data-id="element-4933" data-mp="21dpmd75c3ty1">All expiry dates</option>
              <option value="near" data-id="element-4934" data-mp="21e95gr6q0odn">Near expiry</option>
              <option value="expired" data-id="element-4935" data-mp="1wfo4uqnk42oo">Expired</option>
            </select>
            <select aria-label="Filter by stock status" value={stock} onChange={event => setStock(event.target.value)} className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-id="element-4936" data-mp="2aenuo1mt2nmf">
              <option value="" data-id="element-4937" data-mp="3lvx929r2upgv">All stock status</option>
              <option value="In stock" data-id="element-4938" data-mp="3gtpvjfqln1cf">In stock</option>
              <option value="Low stock" data-id="element-4939" data-mp="2ehbs5xg7022i">Low stock</option>
              <option value="Out of stock" data-id="element-4940" data-mp="2egtmka7n4bhx">Out of stock</option>
              <option value="Near expiry" data-id="element-4941" data-mp="2jfwqfrbvwbob">Near expiry</option>
              <option value="Expired" data-id="element-4942" data-mp="2jiiw1lf7sh3w">Expired</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-4943" data-mp="2ng6yd9j8lc5t">
          <table className="min-w-[1100px] w-full divide-y divide-slate-100" data-id="element-4944" data-mp="2shcsnbsjp7hg">
            <thead className="bg-slate-50" data-id="element-4945" data-mp="1vr3tpsnftzsn">
              <tr data-id="element-4946" data-mp="1vsevjmxboxh4">
                {['Drug Code', 'Medicine Name', 'Batch Number', 'Current Quantity', 'Unit', 'Reorder Level', 'Expiry Date', 'Status', 'Actions'].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4947" data-mp="1c3mm7ydye74n">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4948" data-mp="14itsa2crdb80">
              {rows.map(row => <tr key={row.id} className="align-middle" data-id="element-4949" data-mp="zjplfidb3pku">
                  <td className="px-5 py-3.5 font-mono text-sm font-semibold text-teal-800" data-id="element-4950" data-mp="zmbtdj44073l">
                    {row.drugCode}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4951" data-mp="qju9vcfu9ska">
                    <p className="text-sm font-semibold text-slate-900" data-id="element-4952" data-mp="qlnfqpimo8r8">
                      {row.medicineName}
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-4953" data-mp="gt7y5tb8tm6v">
                      {row.category}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-4954" data-mp="bwhgx2npckjv">
                    {row.batchNumber}
                  </td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-slate-900" data-id="element-4955" data-mp="2tzsqiohlhu8">
                    {row.quantity}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4956" data-mp="1qrpgz3pu0g7t">
                    {row.unit}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4957" data-mp="1vozg5b30w68u">
                    {row.reorderLevel}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4958" data-mp="1lrl03cl0hjh2">
                    {row.expiryDate ? new Date(`${row.expiryDate}T00:00:00`).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4959" data-mp="y20iax0lgs9d">
                    <Status value={row.status} data-id="element-4960" data-mp="y3bjqq6khnxb" />
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4961" data-mp="130k672yr0e4d">
                    <button type="button" onClick={() => setSelectedRow(row)} className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:text-teal-900 focus:outline-none focus:ring-2 focus:ring-teal-600" data-id="element-4962" data-mp="131d3zefqbtad">
                      <EyeIcon className="h-4 w-4" data-id="element-4963" data-mp="aggclj3at4zo" /> Details
                    </button>
                  </td>
                </tr>)}
              {!rows.length && <tr data-id="element-4964" data-mp="jgmox1n9fyx9">
                  <td colSpan={9} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-4965" data-mp="jcyv01dwlai0">
                    No batch inventory matches the selected filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
      {selectedRow && <section aria-live="polite" className="flex flex-col gap-3 rounded-xl border border-teal-200 bg-teal-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-4966" data-mp="132r9ainsalr7">
          <div data-id="element-4967" data-mp="1c4ifdom8kull">
            <p className="text-sm font-bold text-slate-900" data-id="element-4968" data-mp="1c1waxxun6v7l">
              {selectedRow.medicineName} · {selectedRow.batchNumber}
            </p>
            <p className="mt-1 text-sm text-slate-600" data-id="element-4969" data-mp="1jn2vayj21gvm">
              {selectedRow.quantity} {selectedRow.unit} available · status:{' '}
              {selectedRow.status}
              {selectedRow.expiryDate ? ` · expires ${new Date(`${selectedRow.expiryDate}T00:00:00`).toLocaleDateString()}` : ''}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setSelectedRow(null)} data-id="element-4970" data-mp="1okw4m0fnmr04">
            Close details
          </Button>
        </section>}
    </div>;
}
function Status({
  value
}: {
  value: string;
}) {
  const classes = value === 'Out of stock' || value === 'Expired' ? 'bg-red-50 text-red-700' : value === 'Low stock' ? 'bg-amber-50 text-amber-700' : value === 'Near expiry' ? 'bg-orange-50 text-orange-700' : 'bg-emerald-50 text-emerald-700';
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${classes}`} data-id="element-4971" data-mp="d70n4l1a265q">
      {value}
    </span>;
}